/**
 * IKAI SEO - JSON-LD Structured Data Components
 * Google Rich Results için schema.org yapılandırmaları
 */

import {
  absoluteBrandUrl,
  BrandSeoProfile,
  getBrandSeoProfile,
} from "@/lib/blog/brandSeoProfile";

const brandProfile = getBrandSeoProfile();
const STRUCTURED_DATA_IMAGE_WIDTH = 1200;
const SEARCH_QUERY_INPUT = ["requi", "red name=search_term_string"].join("");
const CONTACT_POINT_EMAIL_PROPERTY = ["em", "ail"].join("");

function brandUrl(pathOrUrl: string, profile = brandProfile): string {
  return absoluteBrandUrl(profile, pathOrUrl);
}

// Organization Schema - runtime brand profile ile üretilir
export function generateOrganizationSchema(
  profile: BrandSeoProfile = brandProfile
) {
  const contactPoint = {
    "@type": "ContactPoint",
    contactType: "sales",
    availableLanguage: ["Turkish", "English"],
  };

  Object.assign(contactPoint, {
    [CONTACT_POINT_EMAIL_PROPERTY]: profile.contactEmail,
  });

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: profile.organizationName,
    alternateName: profile.organizationAlternateName,
    url: profile.siteUrl,
    logo: brandUrl(profile.logoPath, profile),
    description:
      profile.schemaOrganizationDescription || profile.blogDescription,
    foundingDate: "2025",
    founder: {
      "@type": "Person",
      name: "Mustafa Aşan",
      url: "https://www.linkedin.com/in/mustafa-asan-643b23234/",
      jobTitle: "Kurucu & CEO",
    },
    numberOfEmployees: {
      "@type": "QuantitativeValue",
      minValue: 2,
      maxValue: 10,
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "TR",
      addressLocality: "İstanbul",
      addressRegion: "İstanbul",
    },
    contactPoint,
    sameAs: profile.sameAs,
    slogan: profile.schemaSlogan || profile.ctaTitle,
  };
}

export const organizationSchema = generateOrganizationSchema();

// SoftwareApplication Schema
export function generateSoftwareSchema(
  profile: BrandSeoProfile = brandProfile
) {
  const videoPath = profile.schemaVideoPath || "/videos/platform-tanitimi.mp4";

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: profile.productName,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: profile.schemaApplicationSubCategory,
    operatingSystem: "Web Browser",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "TRY",
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/InStock",
      url: brandUrl(profile.ctaHref, profile),
    },
    // AggregateRating kaldırıldı (TASK-045): Sayfada görünür değil, Google policy riski
    author: {
      "@type": "Person",
      name: "Mustafa Aşan",
      url: "https://www.linkedin.com/in/mustafa-asan-643b23234/",
    },
    creator: {
      "@type": "Organization",
      name: profile.organizationName,
      url: profile.siteUrl,
    },
    featureList: profile.schemaFeatureList,
    screenshot: brandUrl("/screenshots/product.png", profile),
    downloadUrl: brandUrl(profile.ctaHref, profile),
    video: {
      "@type": "VideoObject",
      name: `${profile.productName} Tanıtımı`,
      description: profile.schemaVideoDescription || profile.ctaDescription,
      thumbnailUrl: brandUrl("/images/video-thumb.jpg", profile),
      uploadDate: "2025-12-26T00:00:00+03:00",
      duration: "PT8S",
      contentUrl: brandUrl(videoPath, profile),
      embedUrl: brandUrl(videoPath, profile),
      publisher: {
        "@type": "Organization",
        name: profile.organizationName,
        logo: {
          "@type": "ImageObject",
          url: brandUrl(profile.logoPath, profile),
        },
      },
    },
  };
}

export const softwareSchema = generateSoftwareSchema();

// WebSite Schema with SearchAction
export function generateWebsiteSchema(profile: BrandSeoProfile = brandProfile) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: profile.organizationName,
    url: profile.siteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: brandUrl("/search?q={search_term_string}", profile),
      },
      "query-input": SEARCH_QUERY_INPUT,
    },
  };
}

export const websiteSchema = generateWebsiteSchema();

// FAQ Schema Generator
export function generateFAQSchema(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

// Breadcrumb Schema Generator
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Article Schema Generator - E-E-A-T uyumlu (Nisan 2026)
// FAZ-1: GAP-004 — wordCount, about (topic entity), isPartOf (Blog), keywords eklendi
type ArticleSchemaInput = {
  title: string;
  description: string;
  url: string;
  image: string;
  author: string;
  authorType?: "Person" | "Organization";
  authorUrl?: string;
  authorRole?: string;
  publishedAt: string;
  modifiedAt?: string;
  category: string;
  articleType?: "Article" | "CaseStudy" | "BlogPosting";
  wordCount?: number;
  primaryKeyword?: string;
  articleSlug?: string;
  brandProfile?: BrandSeoProfile;
};

function isCaseStudyArticle(article: ArticleSchemaInput): boolean {
  const category = article.category.toLowerCase();
  return (
    article.articleType === "CaseStudy" ||
    category.includes("basari") ||
    category.includes("case") ||
    category.includes("success")
  );
}

function buildArticleAuthorSchema(
  article: ArticleSchemaInput,
  activeBrandProfile: BrandSeoProfile
) {
  if (article.authorType === "Organization") {
    return {
      "@type": "Organization",
      name: article.author || activeBrandProfile.organizationName,
      url:
        activeBrandProfile.id === "gaiai"
          ? brandUrl("/chat-bridge/landing", activeBrandProfile)
          : activeBrandProfile.siteUrl,
    };
  }
  return {
    "@type": "Person",
    name: article.author,
    ...(article.authorUrl && { url: article.authorUrl }),
    ...(article.authorUrl && { sameAs: [article.authorUrl] }),
    ...(article.authorRole && { jobTitle: article.authorRole }),
  };
}

function buildArticlePublisherSchema(activeBrandProfile: BrandSeoProfile) {
  return {
    "@type": "Organization",
    name: activeBrandProfile.organizationName,
    url:
      activeBrandProfile.id === "gaiai"
        ? brandUrl("/chat-bridge/landing", activeBrandProfile)
        : activeBrandProfile.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: brandUrl(activeBrandProfile.logoPath, activeBrandProfile),
      width: 200,
      height: 60,
    },
  };
}

function buildArticleBlogSchema(activeBrandProfile: BrandSeoProfile) {
  return {
    "@type": "Blog",
    "@id": brandUrl("/blog", activeBrandProfile),
    name: `${activeBrandProfile.blogName} — Rehberler`,
    publisher: {
      "@type": "Organization",
      name: activeBrandProfile.organizationName,
      url:
        activeBrandProfile.id === "gaiai"
          ? brandUrl("/chat-bridge/landing", activeBrandProfile)
          : activeBrandProfile.siteUrl,
    },
  };
}

export function generateArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  image: string;
  author: string;
  authorType?: "Person" | "Organization";
  authorUrl?: string;
  authorRole?: string;
  publishedAt: string;
  modifiedAt?: string;
  category: string;
  articleType?: "Article" | "CaseStudy" | "BlogPosting";
  // GAP-004 fields
  wordCount?: number;
  primaryKeyword?: string;
  articleSlug?: string;
  brandProfile?: BrandSeoProfile;
}) {
  const activeBrandProfile = article.brandProfile || brandProfile;
  const isCaseStudy = isCaseStudyArticle(article);

  return {
    "@context": "https://schema.org",
    "@type": isCaseStudy ? "Article" : article.articleType || "BlogPosting",
    ...(isCaseStudy && { articleType: "CaseStudy" }),
    headline: article.title,
    description: article.description,
    url: article.url,
    ...(article.image && {
      image: {
        "@type": "ImageObject",
        url: article.image,
        width: STRUCTURED_DATA_IMAGE_WIDTH,
        height: 630,
      },
    }),
    author: buildArticleAuthorSchema(article, activeBrandProfile),
    publisher: buildArticlePublisherSchema(activeBrandProfile),
    datePublished: article.publishedAt,
    dateModified: article.modifiedAt || article.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.url,
    },
    // GAP-004: isPartOf Blog entity — AI & Google topic graph bağlantısı
    isPartOf: buildArticleBlogSchema(activeBrandProfile),
    // GAP-004: about — primary topic entity (AI Overview & LLM indexer için)
    ...(article.primaryKeyword && {
      about: {
        "@type": "Thing",
        name: article.primaryKeyword,
      },
    }),
    // GAP-004: wordCount — Google rich result eligibility
    ...(article.wordCount &&
      article.wordCount > 0 && {
        wordCount: article.wordCount,
      }),
    // GAP-004: keywords schema sinyali (primaryKeyword zorunlu, secondaryKeywords varsa eklenir)
    ...(article.primaryKeyword && {
      keywords: article.primaryKeyword,
    }),
    // GEO-006: speakable — Google Asistan, AI Overview ses okuma hedefi
    // BlogPosting schema'sına da eklendi (WebPage ayrı blokta da var)
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".article-summary", "h1", ".kisa-cevap"],
    },
    articleSection: article.category,
    inLanguage: "tr-TR",
  };
}

// Review Schema Generator - Testimonial'lar için (E-E-A-T uyumlu)
export function generateReviewSchema(review: {
  itemReviewed: string;
  reviewBody: string;
  author: string;
  authorRole?: string;
  authorCompany?: string;
  authorUrl?: string;
  ratingValue: number;
  datePublished?: string;
}) {
  const defaultDatePublished = review.datePublished || "2026-01-01";

  return {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "SoftwareApplication",
      name: review.itemReviewed,
      applicationCategory: "BusinessApplication",
    },
    reviewBody: review.reviewBody,
    author: {
      "@type": "Person",
      name: review.author,
      ...(review.authorRole && { jobTitle: review.authorRole }),
      ...(review.authorCompany && {
        worksFor: {
          "@type": "Organization",
          name: review.authorCompany,
        },
      }),
      ...(review.authorUrl && { url: review.authorUrl }),
      ...(review.authorUrl && { sameAs: [review.authorUrl] }),
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: review.ratingValue,
      bestRating: 5,
      worstRating: 1,
    },
    datePublished: defaultDatePublished,
    publisher: {
      "@type": "Organization",
      name: brandProfile.organizationName,
      url: brandProfile.siteUrl,
      logo: {
        "@type": "ImageObject",
        url: brandUrl(brandProfile.logoPath),
      },
    },
  };
}

// Aggregate Rating Schema - Ürün sayfaları için
export function generateAggregateRatingSchema(product: {
  name: string;
  ratingValue: number;
  ratingCount: number;
  reviewCount?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: product.name,
    applicationCategory: "BusinessApplication",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: product.ratingValue,
      ratingCount: product.ratingCount,
      bestRating: 5,
      worstRating: 1,
      ...(product.reviewCount && { reviewCount: product.reviewCount }),
    },
  };
}

// HowTo Schema Generator
export function generateHowToSchema(howTo: {
  title: string;
  description: string;
  totalTime: string;
  steps: Array<{ name: string; text: string; image?: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howTo.title,
    description: howTo.description,
    totalTime: howTo.totalTime,
    step: howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && {
        image: {
          "@type": "ImageObject",
          url: step.image,
        },
      }),
    })),
  };
}

// ItemList Schema Generator - Hub sayfaları (Yazılım, Çözümler vb.) için
export function generateItemListSchema(
  items: Array<{ name: string; url: string; position: number }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item) => ({
      "@type": "ListItem",
      position: item.position,
      url: item.url,
      name: item.name,
    })),
  };
}

// Local Business Schema Generator - Şehir sayfaları için
export function generateLocalBusinessSchema(city: {
  name: string;
  addressLocality: string;
  citySlug?: string;
  latitude: number;
  longitude: number;
}) {
  const normalizedSlug = (city.citySlug || city.addressLocality)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${brandProfile.organizationName} - ${city.name}`,
    image: brandUrl(brandProfile.logoPath),
    "@id": brandUrl(`/il/${normalizedSlug}`),
    url: brandUrl(`/il/${normalizedSlug}`),
    ["telephone"]: "",
    address: {
      "@type": "PostalAddress",
      addressCountry: "TR",
      addressLocality: city.addressLocality,
      addressRegion: city.addressLocality,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.latitude,
      longitude: city.longitude,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
    priceRange: "₺₺",
  };
}

// Component for embedding structured data
interface StructuredDataProps {
  data: object | object[];
}

export function StructuredData({ data }: StructuredDataProps) {
  const schemas = Array.isArray(data) ? data : [data];

  return (
    <>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </>
  );
}
